import type { ElementType, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const cardVariants = cva('rounded-xl border p-5 flex items-center gap-4', {
  variants: {
    tone: {
      neutral: 'border-border bg-card',
      success: 'border-emerald-200 bg-emerald-50',
      warning: 'border-amber-200 bg-amber-50',
      destructive: 'border-red-200 bg-red-50',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const iconBoxVariants = cva('flex items-center justify-center w-10 h-10 rounded-lg', {
  variants: {
    tone: {
      neutral: 'bg-muted',
      success: 'bg-emerald-100',
      warning: 'bg-amber-100',
      destructive: 'bg-red-100',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const iconVariants = cva('h-5 w-5', {
  variants: {
    tone: {
      neutral: 'text-muted-foreground',
      success: 'text-emerald-600',
      warning: 'text-amber-600',
      destructive: 'text-red-600',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const labelVariants = cva('text-xs leading-tight font-medium', {
  variants: {
    tone: {
      neutral: 'text-muted-foreground',
      success: 'text-emerald-600',
      warning: 'text-amber-600',
      destructive: 'text-red-600',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const valueVariants = cva('text-[22px] font-semibold leading-tight tabular-nums', {
  variants: {
    tone: {
      neutral: 'text-foreground',
      success: 'text-emerald-700',
      warning: 'text-amber-700',
      destructive: 'text-red-700',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

interface StatCardProps extends VariantProps<typeof cardVariants> {
  icon: ElementType;
  /** Plain string for the common case, or a composed node (e.g. text + an InfoHint). */
  label: ReactNode;
  /** A number is locale-formatted automatically; pass a pre-formatted node (e.g. "5 200 zł") to opt out. */
  value: ReactNode;
  loading: boolean;
  loadingTestId?: string;
  /** Applied to the root element, for tests/e2e that need to target one card among several. */
  testId?: string;
}

/** Numbers get `toLocaleString()`; `null`/`undefined` render as "-"; anything else renders as-is. */
function formatStatValue(value: ReactNode): ReactNode {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') return value.toLocaleString();
  return value;
}

function StatCard({
  icon: Icon,
  label,
  value,
  loading,
  loadingTestId,
  tone,
  testId,
}: StatCardProps) {
  return (
    <div className={cardVariants({ tone })} data-testid={testId}>
      <div className={iconBoxVariants({ tone })}>
        <Icon className={iconVariants({ tone })} strokeWidth={1.6} />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className={labelVariants({ tone })}>{label}</span>
        {loading ? (
          <div
            className="h-6 w-12 mt-0.5 rounded bg-muted animate-pulse"
            data-testid={loadingTestId}
          />
        ) : (
          <span className={valueVariants({ tone })}>{formatStatValue(value)}</span>
        )}
      </div>
    </div>
  );
}

export { StatCard };
export type { StatCardProps };
